import { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import {
  flattenKubernetesTargets,
  generateKubernetesSelectionOptions,
  getAllSelectedK8PodObjects,
  getSelectedKubernetesTargets
} from 'components/attacks/create/targets/kubernetes/kubernetes-target-utils'
import { getClientsForTargetingStrategy } from 'components/attacks/create/targets/target-utils'
import {
  activeInfraClientSelector,
  activeK8sClientSelector,
  useInfraClientsStore,
  useK8sClientsStore
} from 'hooks/use-clients'
import { ApplicationClient } from 'types/api/alfi/clients'
import { Client } from 'types/api/attacks/client'
import {
  KubernetesTargetingStrategy,
  TargetingStrategy
} from 'types/api/attacks/details/targeting'
import { K8sClient, K8sObject } from 'types/api/kubernetes/kubernetes'
import { GraphNode, Scenario } from 'types/api/scenarios/scenario'
import { keysToCamelCase } from 'utils/obj'

/**
 * Check whether the target is production by looking at the involved agents
 *
 * @param {TargetingStrategy} targetingStrategy
 * @return {boolean}
 */
export function useIsProductionTarget(
  targetingStrategy: TargetingStrategy,
  clusterId = '',
  namespace = ''
): {
  isProductionTarget: boolean
} {
  const clients = useInfraClientsStore(activeInfraClientSelector)
  const k8sClients = useK8sClientsStore(activeK8sClientSelector)

  if (
    targetingStrategy?.type === ProvenanceTargetType.Container ||
    targetingStrategy?.type === ProvenanceTargetType.ProcessData
  ) {
    return {
      isProductionTarget: (
        getClientsForTargetingStrategy(clients, targetingStrategy) as Client[]
      ).some((c) => isProductionClient(c))
    }
  } else if (targetingStrategy?.type === ProvenanceTargetType.Kubernetes) {
    return {
      isProductionTarget: isProductionFromKubernetesTargets(
        targetingStrategy,
        k8sClients,
        clients,
        clusterId,
        namespace
      )
    }
  } else {
    throw new Error('target strategy unknown')
  }
}

/**
 * Parses a Scenario graph's nodes to determine if it has any production targets
 *
 * @param nodes
 * @param clients
 * @param k8sClients
 */
function scenarioHasProductionTargets(
  nodes: { [key: string]: GraphNode },
  clients: Client[] | ApplicationClient[],
  k8sClients: K8sClient[]
) {
  if (nodes) {
    return Object.entries(nodes).reduce((acc, [, node]) => {
      if (acc || node.type !== 'InfraAttack') {
        return acc
      }

      if (node.type === 'InfraAttack') {
        const targetingStrategy =
          node.attack_configuration.targets[0].targetingStrategy

        if (targetingStrategy.type === ProvenanceTargetType.Kubernetes) {
          return isProductionFromKubernetesTargets(
            keysToCamelCase(targetingStrategy),
            k8sClients,
            clients
          )
        }

        const clientsForTargetingStrategy = getClientsForTargetingStrategy(
          clients,
          targetingStrategy
        ) as Client[]

        return clientsForTargetingStrategy.some((c) => isProductionClient(c))
      }
    }, false)
  }

  return false
}

/**
 * Check whether a scenario has any production targets looking at the involved agents in each graph node
 *
 * @param {Scenario} scenario
 * @return {boolean}
 */
export function useHasProductionTargetsScenario(scenario: Scenario): {
  hasProductionTargets: boolean
} {
  const clients = useInfraClientsStore(activeInfraClientSelector)
  const k8sClients = useK8sClientsStore(activeK8sClientSelector)

  const hasProductionTargets = scenarioHasProductionTargets(
    scenario?.graph?.nodes,
    clients,
    k8sClients
  )

  return { hasProductionTargets }
}

/**
 * Checks whether the client contains production tag
 * @param {Client} c
 * @return {boolean} <code>true</code> if the client is a production client,
 * <code>false</code> otherwise
 */
function isProductionClient(c: Client): boolean {
  return c?.tags?.environment === 'production'
}

function getSelectedObjectsByTargetingStrategy(
  targetingStrategy: KubernetesTargetingStrategy,
  targets: K8sObject[],
  clusterId?: string,
  namespace?: string
) {
  let selectedObjects = []

  if (targetingStrategy?.isAll) {
    selectedObjects = targets
  } else if (targetingStrategy?.names) {
    selectedObjects = targetingStrategy?.names.map(
      (n) =>
        targets?.find(
          (t) =>
            t.name === n.name &&
            t.namespace === n.namespace &&
            t.kind === n.kind &&
            t.clusterId === n.clusterId
        ) //uid is not saved on Service reason why we need to find it
    )
  } else if (targetingStrategy?.ids) {
    selectedObjects = targetingStrategy.ids.map((id) =>
      targets.find((t) => t.uid === id)
    )
  }

  if (clusterId) {
    selectedObjects = selectedObjects.filter((o) => {
      return o.clusterId === clusterId
    })
  }

  if (namespace) {
    selectedObjects = selectedObjects.filter((o) => {
      return o.namespace === namespace
    })
  }

  return new Set(selectedObjects.map((t) => t?.uid).filter(Boolean))
}

/**
 * Checks whether it is a production target by looking at the Kubernetes targets
 *
 * @param {TargetingStrategy} targetingStrategy
 * @param {K8sClient[]} k8sClients
 * @param {Client[]} clients
 * @param {string} clusterId used for FI attacks to limit to a cluster
 * @param {string} namespace used for FI attacks to limit to a namespace
 * @return {boolean} <code>true</code> if is production, <code>false</code>  otherwise
 */
function isProductionFromKubernetesTargets(
  targetingStrategy: TargetingStrategy,
  k8sClients: K8sClient[],
  clients: Client[],
  clusterId?: string,
  namespace?: string
): boolean {
  const kubernetesTargetingStrategy =
    targetingStrategy as KubernetesTargetingStrategy

  const targets = flattenKubernetesTargets(k8sClients)
  const k8sObjects = getSelectedKubernetesTargets(targets, {
    selectedNamespace: null,
    selectedObjects: getSelectedObjectsByTargetingStrategy(
      kubernetesTargetingStrategy,
      targets,
      clusterId,
      namespace
    )
  })

  const targetsByOwners = generateKubernetesSelectionOptions(targets)

  const selectedTargets = Array.from(
    new Set(
      getAllSelectedK8PodObjects(
        new Set(k8sObjects.map((k) => k?.uid)),
        targetsByOwners
      )?.map((h) => h?.hostId)
    )
  )

  return selectedTargets.some((clientId) =>
    clients.some((c) => c.identifier === clientId && isProductionClient(c))
  )
}
